import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import './UserMenu.css';

type UserMenuProps = {
  username?: string;
  onLogout: () => void;
  onShowBusinessCard: () => void;
  onShowProfile: () => void;
  placement?: 'top' | 'rail';
  showLogout?: boolean;
};

const getInitials = (name?: string) => name
  ?.split(' ')
  .map((word) => word.charAt(0).toUpperCase())
  .join('')
  .slice(0, 2) || '?';

export default function UserMenu({
  username,
  onLogout,
  onShowBusinessCard,
  onShowProfile,
  placement = 'top',
  showLogout = true,
}: UserMenuProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  const runAndClose = (action: () => void) => {
    setIsOpen(false);
    action();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsOpen(false);
    onLogout();
    navigate('/login');
  };

  return (
    <div className={`user-menu user-menu--${placement}`} ref={menuRef}>
      <button
        type="button"
        className="user-menu-trigger"
        onClick={() => setIsOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('utility.user_menu', { defaultValue: 'Používateľské menu' })}
        title={username}
      >
        {getInitials(username)}
      </button>

      {isOpen && (
        <div className="user-menu-dropdown" role="menu">
          <div className="user-menu-header" title={username}>{username}</div>
          <button type="button" role="menuitem" onClick={() => window.alert('Upgrade plan clicked')}>
            <i className="bi bi-award-fill" aria-hidden="true" />
            <span>{t('upgrade_plan')}</span>
          </button>
          <button type="button" role="menuitem" onClick={() => runAndClose(onShowProfile)}>
            <i className="bi bi-gear" aria-hidden="true" />
            <span>{t('settings')}</span>
          </button>
          <button type="button" role="menuitem" onClick={() => runAndClose(onShowBusinessCard)}>
            <i className="bi bi-person-vcard" aria-hidden="true" />
            <span>{t('business_card')}</span>
          </button>
          <div className="user-menu-language">
            <span>{t('language')}</span>
            <LanguageSwitcher />
          </div>
          <button type="button" role="menuitem" onClick={() => window.alert('Help clicked')}>
            <i className="bi bi-question-circle" aria-hidden="true" />
            <span>{t('help')}</span>
          </button>
          {showLogout && (
            <>
              <span className="user-menu-divider" />
              <button type="button" role="menuitem" className="user-menu-logout" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right" aria-hidden="true" />
                <span>{t('logout') || 'Log out'}</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
